import cn from 'classnames';
import RichText from 'components/shared/richtext';
import { useGlobalModules, useSettings } from 'contexts';
import { useKeenSlider } from 'keen-slider/react';
import { useEffect, useState } from 'react';
import useIsMobile from 'hooks/utils/useIsMobile';
import SiteLink from 'components/shared/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import styles from './notificationBar.module.scss';
import 'keen-slider/keen-slider.min.css';

const SalonNotification = ({ translations, currentSalon, locale, isActive = true }) => {
  const { salonSelectedMessage, selectASalonMessage } = translations?.notificationsBar;

  let href = addLocaleToHref('salon-finder', locale);

  if (currentSalon?.salonNo) {
    href += `?salonNo=${currentSalon.salonNo}`;
  }

  const linkTabIndex = isActive ? undefined : -1;

  if (currentSalon?.salonName) {
    const parts = salonSelectedMessage.split('{{salonName}}');

    return (
      <p>
        {parts[0]}{' '}
        <SiteLink className={styles.salonLink} href={href} tabIndex={linkTabIndex}>
          {currentSalon.salonName}
        </SiteLink>{' '}
        {parts[1]}
      </p>
    );
  }

  return (
    <p>
      <SiteLink className={styles.salonLink} href={href} tabIndex={linkTabIndex}>
        {selectASalonMessage}
      </SiteLink>
    </p>
  );
};

const NotificationBar = ({ locale }) => {
  const globalModules = useGlobalModules();
  const { notifications, translations } = globalModules;
  const [isPaused, setIsPaused] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const isMobile = useIsMobile();
  const { currentSalon } = useSettings();

  const notificationEntries = Object.entries(notifications || {});

  const sliderOptions = {
    loop: true,
    initial: 0,
    slidesPerView: 1,
    vertical: true,
    breakpoints: {
      '(max-width: 900px)': {
        vertical: false,
      },
    },
    slideChanged: slider => {
      setCurrentSlide(slider.track.details.rel);
    },
  };

  const autoplay = slider => {
    let timeout;
    let mouseOver = false;

    function clearNextTimeout() {
      clearTimeout(timeout);
    }

    function nextTimeout() {
      clearTimeout(timeout);
      if (mouseOver) return;
      timeout = setTimeout(() => {
        slider.next();
      }, 5000);
    }

    slider.on('created', () => {
      slider.container.addEventListener('mouseover', () => {
        mouseOver = true;
        clearNextTimeout();
      });
      slider.container.addEventListener('mouseout', () => {
        mouseOver = false;
        nextTimeout();
      });
      nextTimeout();
    });

    slider.on('dragStarted', clearNextTimeout);
    slider.on('animationEnded', nextTimeout);
    slider.on('updated', nextTimeout);

    // Expose nextTimeout function so we can call it externally
    slider.pause = flag => {
      if (flag) {
        clearNextTimeout();
      } else {
        nextTimeout();
      }
    };

    return {
      destroy: () => {
        clearNextTimeout();
      },
    };
  };

  const [sliderRef, instanceRef] = useKeenSlider(sliderOptions, [autoplay]);

  useEffect(() => {
    if (instanceRef.current && isMobile !== null) {
      instanceRef.current.update({
        ...sliderOptions,
        vertical: !isMobile,
      });
    }
  }, [isMobile, instanceRef]);

  useEffect(() => {
    if (instanceRef.current) {
      instanceRef.current.pause(isPaused);
    }
  }, [isPaused, instanceRef]);

  const hasAnnouncementBar = globalModules?.settings?.locale?.hasAnnouncementBar;

  useEffect(() => {
    document.body.style.setProperty('--header-height', hasAnnouncementBar ? '8rem' : '5rem');
  }, [hasAnnouncementBar]);

  if (!notificationEntries.length || !hasAnnouncementBar) return null;

  return (
    <div className={styles.notificationBarContainer} role="region" aria-label="Notifications">
      <div
        className={cn(styles.notificationBar, 'keen-slider')}
        ref={sliderRef}
        aria-live="off" // Disable default live region behavior on slider
        role="group"
        aria-label={`Notification carousel, ${notificationEntries.length} notifications`}
      >
        <div
          className={cn(styles.notification, 'keen-slider__slide')}
          key="salonNotification"
          role="group"
          aria-label={`Notification 1 of ${notificationEntries.length + 1}`}
          aria-hidden={currentSlide !== 0}
        >
          <SalonNotification
            translations={translations}
            currentSalon={currentSalon}
            locale={locale}
            isActive={currentSlide === 0}
          />
        </div>
        {notificationEntries.map(([, notification], index) => (
          <div
            className={cn(styles.notification, 'keen-slider__slide')}
            key={notification.id}
            role="group"
            aria-label={`Notification ${index + 2} of ${notificationEntries.length + 1}`}
            aria-hidden={index + 1 !== currentSlide}
          >
            <RichText className={styles.richText} text={notification.content.json} />
          </div>
        ))}
      </div>

      <div className={styles.outlineIndicator} />

      {/* Live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
        {currentSlide === 0 && (
          <SalonNotification
            translations={translations}
            currentSalon={currentSalon}
            locale={locale}
          />
        )}

        {currentSlide > 0 && notificationEntries[currentSlide - 1] && (
          <RichText
            className={styles.richText}
            text={notificationEntries[currentSlide - 1][1].content.json}
          />
        )}
      </div>

      <button
        type="button"
        className={styles.pauseButton}
        onClick={() => setIsPaused(!isPaused)}
        aria-label={`${isPaused ? 'Resume' : 'Pause'} notification carousel`}
      >
        {isPaused ? 'Resume' : 'Pause'}
      </button>
    </div>
  );
};

export default NotificationBar;
